'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { fetchAllPaddles, buildCategories_list, filterByCategory, type Paddle, type CategoryInfo } from '@/lib/services/paddleService';
import { useComparison } from '@/contexts/ComparisonContext';

interface CategoryPageProps {
  params: { slug: string };
}

export default function CategoryPage({ params }: CategoryPageProps) {
  const { addItem, removeItem, isSelected } = useComparison();
  const [allPaddles, setAllPaddles] = useState<Paddle[]>([]);
  const [categoryPaddles, setCategoryPaddles] = useState<Paddle[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllPaddles().then(data => {
      setAllPaddles(data);
      setCategoryPaddles(filterByCategory(data, params.slug));
      setCategories(buildCategories_list(data));
      setLoading(false);
    });
  }, [params.slug]);

  const category = categories.find(c => c.slug === params.slug) || { name: params.slug, slug: params.slug, icon: '', count: 0, description: '' };

  return (
    <div className="min-h-screen" style={{ background: '#F2F2F2' }}>
      <Navigation />

      {/* Header */}
      <section className="pt-28 pb-16 px-4 relative overflow-hidden" style={{ background: '#FFFFFF' }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 75% -5%, rgba(217,0,18,0.03) 0%, transparent 55%)',
        }} />
        <div className="max-w-container mx-auto relative z-10">
          <div className="breadcrumb mb-5">
            <Link href="/" style={{ textDecoration: 'none' }}>Home</Link>
            <span className="breadcrumb-sep">/</span>
            <Link href="/categories" style={{ textDecoration: 'none' }}>Categories</Link>
            <span className="breadcrumb-sep">/</span>
            <span style={{ color: 'rgba(13,13,13,0.52)' }}>{category.name}</span>
          </div>
          <div className="premium-chip accent mb-5">{category.name}</div>
          <h1
            className="font-black mb-3"
            style={{
              fontSize: 'clamp(30px, 4.5vw, 52px)',
              letterSpacing: '-0.045em',
              color: '#0D0D0D',
              lineHeight: 1.04,
            }}
          >
            {category.name} Paddles
          </h1>
          <p style={{ color: 'rgba(13,13,13,0.42)', fontSize: '15px', lineHeight: 1.72 }}>
            {category.description} —{' '}
            <span style={{ color: '#D90012', fontWeight: 700 }}>{categoryPaddles.length} paddles</span>
          </p>
        </div>
      </section>

      <div className="max-w-container mx-auto px-4">
        <div className="premium-divider" />
      </div>

      <main className="max-w-container mx-auto px-4 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div style={{ color: 'rgba(13,13,13,0.4)', fontSize: '14px' }}>Loading paddles…</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categoryPaddles.map((paddle, i) => {
              const selected = isSelected(paddle.id);
              return (
                <div
                  key={paddle.id}
                  className="product-card"
                  style={{
                    border: selected ? '1.5px solid rgba(217,0,18,0.28)' : '1px solid rgba(0,0,0,0.07)',
                    boxShadow: selected
                      ? '0 4px 20px rgba(217,0,18,0.08)'
                      : 'var(--shadow-card)',
                  }}
                  onMouseEnter={e => {
                    if (!selected) {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(217,0,18,0.14)';
                      (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card-hover)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!selected) {
                      (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,0,0,0.07)';
                      (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card)';
                    }
                  }}
                >
                  <Link href={`/paddles/${paddle.slug}`} className="block flex-1" style={{ textDecoration: 'none' }}>
                    <div className="product-card-image" style={{ paddingBottom: '80%' }}>
                      <div
                        className="absolute top-2.5 left-2.5 z-10 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                          background: 'rgba(255,255,255,0.95)',
                          color: 'rgba(13,13,13,0.4)',
                          fontSize: '9px',
                          fontWeight: 700,
                          border: '1px solid rgba(0,0,0,0.07)',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                      >
                        {i + 1}
                      </div>
                      {paddle.isNew && (
                        <div className="absolute top-2.5 right-2.5 z-10">
                          <span
                            className="font-bold px-2 py-0.5 rounded-full"
                            style={{
                              background: '#D90012',
                              color: '#fff',
                              fontSize: '9px',
                              letterSpacing: '0.09em',
                              textTransform: 'uppercase',
                            }}
                          >NEW</span>
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={paddle.image}
                          alt={paddle.imageAlt}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                        />
                      </div>
                    </div>
                    <div className="product-card-body">
                      <div className="product-card-brand">{paddle.brand}</div>
                      <div className="product-card-name">{paddle.name}</div>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-sm font-black tabular-nums" style={{ color: '#0D0D0D', letterSpacing: '-0.025em' }}>{paddle.priceFormatted}</span>
                        <span className="badge-red">{paddle.score} pts</span>
                      </div>
                      {paddle.firepowerTier && (
                        <div className="text-xs mt-1.5 truncate" style={{ color: 'rgba(13,13,13,0.3)', fontSize: '10px' }}>{paddle.firepowerTier}</div>
                      )}
                    </div>
                  </Link>
                  <div className="product-card-footer">
                    <button
                      onClick={() => selected ? removeItem(paddle.id) : addItem(paddle)}
                      className={`add-compare-btn${selected ? ' selected' : ''}`}
                    >
                      {selected ? '✓ Added' : '+ Add to Compare'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
